import React, { useState, useEffect, useRef } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { getGoogleMapsApiKey } from '../config/api';
import { Search, MapPin, Navigation, X } from 'lucide-react';

interface LocationData {
  latitude: number;
  longitude: number;
  address: string;
  location: string; // City, State format
}

interface LocationPickerProps {
  onLocationSelect: (location: LocationData) => void;
  initialLocation?: LocationData | null;
  onClose?: () => void;
  showModal?: boolean;
}

const LocationPicker: React.FC<LocationPickerProps> = ({ 
  onLocationSelect, 
  initialLocation,
  onClose,
  showModal = false
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [selectedPosition, setSelectedPosition] = useState<{ lat: number; lng: number } | null>(
    initialLocation ? { lat: initialLocation.latitude, lng: initialLocation.longitude } : null
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>({ lat: 22.5726, lng: 88.3639 }); // Default to Kolkata
  const [locationDetails, setLocationDetails] = useState<LocationData | null>(initialLocation || null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [marker, setMarker] = useState<google.maps.Marker | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requireAddress = true; // Address lookup always enabled
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Initialize Google Maps
  useEffect(() => {
    const initMap = async () => {
      try {
        console.log('🗺️ LocationPicker: Starting map initialization...');
        setLoading(true);
        setError(null);

        const apiKey = getGoogleMapsApiKey();
        console.log('🔑 LocationPicker API Key loaded:', apiKey ? 'Present' : 'Missing');
        console.log('🔑 LocationPicker API Key value:', apiKey);
        
        if (!apiKey || apiKey === 'YOUR_GOOGLE_MAPS_API_KEY') {
          const errorMsg = `Google Maps API key not configured. Please add VITE_GOOGLE_MAPS_API_KEY to your environment variables. Current value: "${apiKey}" | Type: ${typeof apiKey} | Length: ${apiKey ? apiKey.length : 'N/A'}`;
          console.error('❌ LocationPicker API Key Error:', errorMsg);
          setError(errorMsg);
          setLoading(false);
          return;
        }

        console.log('⚙️ LocationPicker: Creating Google Maps Loader...');
        const loader = new Loader({
          apiKey: apiKey,
          version: 'weekly',
          libraries: ['places']
        });

        console.log('📡 LocationPicker: Loading Google Maps API...');
        const google = await loader.load();
        console.log('✅ LocationPicker: Google Maps API loaded successfully');
        
        if (!mapRef.current) {
          // Wait one frame to allow the container to mount (since UI might show a loading placeholder first)
          await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
        }
        if (!mapRef.current) {
          console.error('❌ LocationPicker: mapRef is null after waiting; container not mounted or visible.');
          setError('Map container not available.');
          setLoading(false);
          return;
        }

        console.log('🏗️ LocationPicker: Creating Google Map instance...');
        console.log('📍 LocationPicker Map center:', mapCenter);
        console.log('📍 LocationPicker Map container element:', mapRef.current);

        const mapInstance = new google.maps.Map(mapRef.current, {
          center: mapCenter,
          zoom: 9,
          mapTypeId: google.maps.MapTypeId.ROADMAP,
          mapTypeControl: true,
          mapTypeControlOptions: {
            position: google.maps.ControlPosition.TOP_RIGHT,
            style: google.maps.MapTypeControlStyle.DROPDOWN_MENU
          },
          streetViewControl: true,
          fullscreenControl: true,
          zoomControl: true,
        });

        console.log('✅ LocationPicker: Google Map instance created successfully');

        // Initialize Places service
        // Using Google Autocomplete only; no manual PlacesService list

        // Attach Places Autocomplete to the search input as a direct text→coordinates path
        if (searchInputRef.current) {
          const autocomplete = new google.maps.places.Autocomplete(searchInputRef.current, {
            fields: ['geometry', 'formatted_address', 'name', 'place_id']
          });

          autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();
            if (place && place.geometry && place.geometry.location) {
              const lat = place.geometry.location.lat();
              const lng = place.geometry.location.lng();
              const formatted = place.formatted_address || `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
              // const name = place.name || formatted.split(',')[0] || 'Unknown location';

              // Update map center/zoom and use common handler to move/create marker & details
              setSearchQuery(formatted);
              setMapCenter({ lat, lng });
              if (mapInstance) {
                mapInstance.setCenter({ lat, lng });
                mapInstance.setZoom(12);
              }

              // Ensure marker follows selected place and details are set
              handleLocationClick(lat, lng);
            } else if (place && place.formatted_address) {
              // Fallback to geocoding if geometry missing
              geocodeAddress(place.formatted_address, mapInstance);
            }
          });
        }

        // Add click listener to map
        mapInstance.addListener('click', (event: google.maps.MapMouseEvent) => {
          if (event.latLng) {
            const lat = event.latLng.lat();
            const lng = event.latLng.lng();
            handleLocationClick(lat, lng);
          }
        });

        // Add initial marker if position exists
        if (selectedPosition) {
          const markerInstance = new google.maps.Marker({
            position: selectedPosition,
            map: mapInstance,
            title: 'Selected Location',
            draggable: true
          });

          markerInstance.addListener('dragend', () => {
            const position = markerInstance.getPosition();
            if (position) {
              const lat = position.lat();
              const lng = position.lng();
              handleLocationClick(lat, lng);
            }
          });

          setMarker(markerInstance);
        }

        console.log('🎯 LocationPicker: Setting map state and completing initialization...');
        setMap(mapInstance);
        setLoading(false);
        console.log('🎉 LocationPicker initialization completed successfully!');

      } catch (err) {
        console.error('❌ LocationPicker Error loading Google Maps:', err);
        const error = err as Error;
        console.error('❌ LocationPicker Error details:', {
          name: error?.name,
          message: error?.message,
          stack: error?.stack
        });
        setError(`Failed to load Google Maps: ${error?.message || 'Unknown error'}. Please check your API key and try again.`);
        setLoading(false);
      }
    };

    console.log('🚀 LocationPicker: Starting map initialization...');
    initMap();
  }, []);
  // Geocode a free-text address to coordinates using JS Geocoder
  const geocodeAddress = (query: string, targetMap?: google.maps.Map | null) => {
    try {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: query }, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK && results && results[0]) {
          const loc = results[0].geometry.location;
          const lat = loc.lat();
          const lng = loc.lng();
          setSelectedPosition({ lat, lng });
          setMapCenter({ lat, lng });
          setSearchQuery(results[0].formatted_address || query);
          // No custom results list
          if (targetMap || map) {
            (targetMap || map)!.setCenter({ lat, lng });
          }
          
          // Create location data based on address requirement
          const locationData: LocationData = requireAddress ? {
            latitude: lat,
            longitude: lng,
            address: results[0].formatted_address || `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
            location: results[0].formatted_address?.split(',')[0] || 'Unknown location'
          } : {
            latitude: lat,
            longitude: lng,
            address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
            location: 'Selected coordinates'
          };
          setLocationDetails(locationData);
        } else {
          console.warn('Geocode failed:', status);
        }
      });
    } catch (err) {
      console.error('Geocode error:', err);
    }
  };


  // Get user's current location
  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          setMapCenter({ lat, lng });
          handleLocationClick(lat, lng);
          if (map) {
            map.setCenter({ lat, lng });
          }
        },
        (error) => {
          console.warn('Geolocation error:', error);
        }
      );
    }
  };

  // Removed custom text search; Google Autocomplete handles suggestions

  // Reverse geocoding to get address from coordinates using Google Geocoding API
  const reverseGeocode = async (lat: number, lng: number) => {
    // If address is not required, create simple location data
    if (!requireAddress) {
      const locationData: LocationData = {
        latitude: lat,
        longitude: lng,
        address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
        location: 'Selected coordinates'
      };
      setLocationDetails(locationData);
      return locationData;
    }

    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${getGoogleMapsApiKey()}`
      );
      const result = await response.json();
      
      if (result.status === 'OK' && result.results.length > 0) {
        const address = result.results[0].formatted_address;
        const addressComponents = result.results[0].address_components;
        
        let city = '';
        let state = '';
        
        addressComponents.forEach((component: any) => {
          if (component.types.includes('locality')) {
            city = component.long_name;
          } else if (component.types.includes('administrative_area_level_1')) {
            state = component.long_name;
          }
        });
        
        const location = `${city}${city && state ? ', ' : ''}${state}`;
        
        const locationData: LocationData = {
          latitude: lat,
          longitude: lng,
          address: address,
          location: location || address.split(',')[0] || 'Unknown location'
        };
        
        setLocationDetails(locationData);
        return locationData;
      }
    } catch (error) {
      console.error('Reverse geocoding error:', error);
    }
    
    // Fallback if reverse geocoding fails
    const fallbackLocation: LocationData = {
      latitude: lat,
      longitude: lng,
      address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
      location: 'Selected location'
    };
    setLocationDetails(fallbackLocation);
    return fallbackLocation;
  };

  // Handle map click
  const handleLocationClick = async (lat: number, lng: number) => {
    setSelectedPosition({ lat, lng });
    
    // Update marker position
    if (marker) {
      marker.setPosition({ lat, lng });
    } else if (map) {
      const newMarker = new google.maps.Marker({
        position: { lat, lng },
        map: map,
        title: 'Selected Location',
        draggable: true
      });

      newMarker.addListener('dragend', () => {
        const position = newMarker.getPosition();
        if (position) {
          const newLat = position.lat();
          const newLng = position.lng();
          handleLocationClick(newLat, newLng);
        }
      });

      setMarker(newMarker);
    }
    
    // Only call reverse geocoding if address is required
    if (requireAddress) {
      await reverseGeocode(lat, lng);
    } else {
      // Create simple location data without address lookup
      const locationData: LocationData = {
        latitude: lat,
        longitude: lng,
        address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
        location: 'Selected coordinates'
      };
      setLocationDetails(locationData);
    }
  };

  // Handle search input - Autocomplete widget handles suggestions
  const handleSearchInput = (value: string) => {
    setSearchQuery(value);
  };

  // Removed custom selection; Autocomplete listener updates map

  // Handle confirm selection
  const handleConfirmLocation = () => {
    if (locationDetails) {
      onLocationSelect(locationDetails);
      if (onClose) onClose();
    }
  };

  // No debouncer cleanup needed

  const mapContent = (
    <div className="space-y-4">
      {/* Address Requirement Toggle removed as requested */}

      {/* Search Bar */}
      <div className="relative">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder={requireAddress ? "Search for a location..." : "Search for coordinates or click map..."}
              value={searchQuery}
              onChange={(e) => handleSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (searchQuery.trim()) {
                    geocodeAddress(searchQuery.trim(), map);
                  }
                }
              }}
              ref={searchInputRef}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dpn-pink focus:border-transparent outline-none font-lucy-rose"
            />
          </div>
          <button
            onClick={getCurrentLocation}
            className="px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors flex items-center space-x-2 font-lucy-rose"
            title="Use current location"
          >
            <Navigation className="h-5 w-5" />
            <span className="hidden sm:inline">Current</span>
          </button>
          <button
            onClick={() => searchQuery.trim() && geocodeAddress(searchQuery.trim(), map)}
            className="px-4 py-3 bg-gray-800 hover:bg-black text-white rounded-lg transition-colors flex items-center space-x-2 font-lucy-rose"
            title="Find by address"
          >
            Find
          </button>
        </div>
        
        {/* Removed custom Search Results - Google Autocomplete dropdown is used */}
      </div>

      {/* Map Container (always render map div; overlay loading/error) */}
      <div className="h-80 w-full rounded-lg overflow-hidden border border-gray-300 relative">
        <div ref={mapRef} className="w-full h-full" />

        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100/80 backdrop-blur-sm z-10">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading Google Maps...</p>
            </div>
          </div>
        )}

        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-red-50/90 rounded-lg border border-red-200 z-10">
            <div className="text-center px-4">
              <div className="h-12 w-12 bg-red-500 rounded-full mx-auto mb-4 flex items-center justify-center">
                <span className="text-white text-sm font-bold">!</span>
              </div>
              <p className="text-red-600 mb-2">{error}</p>
              <p className="text-sm text-red-500">Please check your Google Maps API key configuration.</p>
            </div>
          </div>
        )}
      </div>

      {/* Selected Location Info */}
      {locationDetails && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <MapPin className="h-5 w-5 text-green-600 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-lucy-rose text-sm font-medium text-green-800 mb-1">
                Selected Location
              </h4>
              <p className="font-lucy-rose text-sm text-green-700 mb-2">
                <strong>Location:</strong> {locationDetails.location}
              </p>
              {requireAddress && (
                <p className="font-lucy-rose text-xs text-green-600">
                  <strong>Address:</strong> {locationDetails.address}
                </p>
              )}
              <p className="font-lucy-rose text-xs text-green-600 mt-1">
                <strong>Coordinates:</strong> {locationDetails.latitude.toFixed(6)}, {locationDetails.longitude.toFixed(6)}
              </p>
              {!requireAddress && (
                <p className="font-lucy-rose text-xs text-green-500 mt-1 italic">
                  Address lookup disabled - coordinates only mode
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3">
        {onClose && (
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 font-lucy-rose transition-colors"
          >
            Cancel
          </button>
        )}
        <button
          onClick={handleConfirmLocation}
          disabled={!locationDetails}
          className={`px-6 py-2 rounded-lg font-lucy-rose transition-colors ${
            locationDetails
              ? 'bg-dpn-pink hover:bg-dpn-raspberry text-white'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          Confirm Location
        </button>
      </div>
    </div>
  );

  if (showModal) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 relative max-h-[90vh] overflow-y-auto">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
          
          <div className="mb-6">
            <h2 className="font-runtime text-2xl font-bold text-black mb-2">
              Select Event Location
            </h2>
            <p className="font-lucy-rose text-gray-600">
              Search for a location or click on the map to select coordinates
            </p>
          </div>
          
          {mapContent}
        </div>
      </div>
    );
  }

  return mapContent;
};

export default LocationPicker;